import React, { useMemo } from 'react';
import { Redirect, Tabs } from 'expo-router';
import type { Href } from 'expo-router';
import { View, ActivityIndicator } from 'react-native';
import { useAuth } from '../../src/context/AuthContext';
import { pathForPhase } from '../../src/navigation/phaseRedirect';
import { useTabAccess } from '../../src/hooks/useTabAccess';
import { useTranslation } from '../../src/hooks/useTranslation';
import { buildTabConfig } from '../../src/navigation/tabConfig';
import type { ErrorBoundaryProps } from 'expo-router';
import { ErrorFallback } from '../../src/components/ErrorBoundary';

/**
 * Route-level boundary for the tab screens (FE-22): a crash in a tab screen
 * outside the WebAppShell content boundary (e.g. in the screen's own hooks)
 * is contained here instead of replacing the whole app. `retry` re-renders
 * the route; "Go to dashboard" and "Reload" are always offered.
 */
export function ErrorBoundary({ retry }: ErrorBoundaryProps) {
  return <ErrorFallback scope="screen" onRetry={() => { void retry(); }} />;
}

export default function TabsLayout() {
  const { t } = useTranslation();
  const { phase } = useAuth();
  const {
    user,
    chatTitle,
    showDocuments,
    showStudents,
    showProgress,
    isSuper,
    isSchool,
  } = useTabAccess();

  // Compute derived values and hooks before early returns
  const chatLabel = chatTitle || t('navigation.chat');

  // Detect B2B users (has school_id)
  const isB2BStudent = Boolean(user && (user as any).school_id && !isSchool);
  const isB2BAdmin = Boolean(isSchool);

  // All hooks must be called before early returns (Rules of Hooks)
  const { tabItems } = useMemo(
    () =>
      buildTabConfig({
        t,
        chatLabel,
        user: Boolean(user),
        showProgress: Boolean(showProgress),
        showDocuments: Boolean(showDocuments),
        showStudents: Boolean(showStudents),
        showMyDocuments: Boolean(user),
        // New flags for 5-tab structure
        isB2BAdmin,
        isB2BStudent,
        isSuperAdmin: isSuper,
      }),
    [chatLabel, showDocuments, showProgress, showStudents, t, user, isB2BAdmin, isB2BStudent, isSuper]
  );

  const screenOptions = useMemo(() => ({ headerShown: false }), []);

  // Early returns after all hooks are called
  if (phase === 'loading') {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  if (phase !== 'authenticated') {
    return <Redirect href={pathForPhase(phase) as Href} />;
  }

  return (
    <Tabs
      screenOptions={screenOptions}
      // Web-only app (D1): WebAppShell renders the navigation, so the tab
      // navigator never draws its own bar.
      tabBar={() => null}
    >
      {tabItems.map((tab) => (
        <Tabs.Screen
          key={tab.key}
          name={tab.key}
          options={{ title: tab.label }}
        />
      ))}
    </Tabs>
  );
}
